import { defineComponent as _defineComponent } from 'vue'
import type { SnowplowContext } from '@/helpers/tracking/providers';
import { BuyBoxFilterOptions } from '@/stores/useUserStoreTypes';

import { computed, onMounted } from 'vue';
import { useI18n } from '@/helpers/composables/useI18n';
import { storeToRefs } from 'pinia';
import { useRoutingStore } from '@/stores/pinia/useRoutingStore';
// EXP_LINEAR_TV
import { useExperiment } from '@/libraries/experiment';
// EXP_LINEAR_TV
// EXP_PROVIDER_BUYBOX_V2
import MyServicesChip from '@/components/experiments/ProviderBuyboxExp/components/MyServicesChip.vue';
import { useProviderBuyboxFilter } from '@/components/experiments/ProviderBuyboxExp/composables/useProviderBuyboxFilter';
// EXP_PROVIDER_BUYBOX_V2


export default /*#__PURE__*/_defineComponent({
  __name: 'ListBuyBoxFilter',
  props: {
    buyBoxFilterOptions: null,
    additionalContexts: null,
    isTitleUpcoming: { type: Boolean },
    buyboxFilterType: null,
    isInternationalOffersActive: { type: Boolean },
    titleId: null
  },
  emits: ["filterClicked"],
  setup(__props: any, { emit }: { emit: ({
	(
		e: 'filterClicked',
		{
			value,
			nonInteraction,
		}: {
			value: BuyBoxFilterOptions;
			nonInteraction?: boolean;
		}
	): void;
}), expose: any, slots: any, attrs: any }) {

const props = __props as {
	buyBoxFilterOptions: BuyBoxFilterOptions[];
	additionalContexts: SnowplowContext[];
	isTitleUpcoming: boolean;
	buyboxFilterType: BuyBoxFilterOptions;
	isInternationalOffersActive?: boolean;
	// EXP_PROVIDER_BUYBOX_V2: used to reset the My Services modal state per title
	titleId?: string;
	// EXP_PROVIDER_BUYBOX_V2
};

const HiddenHorizontalScrollbar = () => import('@/components/HiddenHorizontalScrollbar.vue');

const { tc } = useI18n();

const { activeRoute } = storeToRefs(useRoutingStore());




// EXP_PROVIDER_BUYBOX_V2
const { handleMyServicesChipClick, myServicesChip, providerBuyboxExpVariant } = useProviderBuyboxFilter({
	isInternationalOffersActive: () => !!props.isInternationalOffersActive,
	titleId: () => props.titleId,
});
// EXP_PROVIDER_BUYBOX_V2

// EXP_LINEAR_TV
const { activeVariant: linearTVVariant } = useExperiment('EXP_LINEAR_TV', 'local');
const isLinearTVVariant1 = computed(() => linearTVVariant.value === 'variant_1');
const isLinearTVVariant2 = computed(() => linearTVVariant.value === 'variant_2');
const showLiveTvChip = computed(() => isLinearTVVariant1.value || isLinearTVVariant2.value);
// EXP_LINEAR_TV

const filterOptions = computed(() => {
	const myServicesOption = myServicesChip.value
		? [{ label: myServicesChip.value.label, value: BuyBoxFilterOptions.MY_SERVICES }]
		: [];
	const freeOption = { label: tc('WEBAPP_FREE'), value: BuyBoxFilterOptions.FREE };

	// EXP_PROVIDER_BUYBOX_V2: variant_1 and variant_3 put Free before My Services
	const afterAll = ['variant_1', 'variant_3'].includes(providerBuyboxExpVariant.value ?? '')
		? [freeOption, ...myServicesOption]
		: [...myServicesOption, freeOption];
	// EXP_PROVIDER_BUYBOX_V2

	// EXP_LINEAR_TV:
	// control keeps the old "Free TV" chip in its original position; variant_1 and variant_2 get the new "Live TV" chip at the end
	const fastTvOption = {
		label: tc('WEBAPP_FREE_TV'),
		value: BuyBoxFilterOptions.FAST_TV,
	};
	const liveTvOption = {
		label: tc('WEBAPP_LIVE_TV'),
		value: BuyBoxFilterOptions.LINEAR_TV,
	};
	// EXP_LINEAR_TV

	const options = [
		{
			label: tc('WEBAPP_ALL'),
			value: BuyBoxFilterOptions.ALL,
		},
		{
			label: tc('WEBAPP_SUBSCRIPTION'),
			value: BuyBoxFilterOptions.SUBSCRIPTION,
		},
		...afterAll,
		// EXP_LINEAR_TV
		...(showLiveTvChip.value ? [] : [fastTvOption]),
		// EXP_LINEAR_TV
		{
			label: tc('WEBAPP_RENT'),
			value: BuyBoxFilterOptions.RENT,
		},
		{
			label: tc('WEBAPP_BUY'),
			value: BuyBoxFilterOptions.BUY,
		},
		{
			label: tc('WEBAPP_CINEMA'),
			value: BuyBoxFilterOptions.CINEMA,
		},
		{
			label: tc('WEBAPP_DISC'),
			value: BuyBoxFilterOptions.PHYSICAL,
		},
		// EXP_LINEAR_TV
		...(showLiveTvChip.value ? [liveTvOption] : []),
		// EXP_LINEAR_TV
	];

	return options.filter(option => {
		return (
			props.buyBoxFilterOptions.includes(option.value) ||
			[
				BuyBoxFilterOptions.ALL,
				props.isTitleUpcoming ? BuyBoxFilterOptions.SUBSCRIPTION : null,
				BuyBoxFilterOptions.FREE,
				myServicesChip.value ? BuyBoxFilterOptions.MY_SERVICES : null,
			].includes(option.value)
		);
	});
});

const handleFilterClick = (value: BuyBoxFilterOptions) => {
	emit('filterClicked', { value });
};

// EXP_PROVIDER_BUYBOX_V2
async function handleMyServicesClick() {
	const isCurrentlyActive = props.buyboxFilterType === BuyBoxFilterOptions.MY_SERVICES;
	const shouldActivate = await handleMyServicesChipClick('list', isCurrentlyActive);
	if (shouldActivate) {
		emit('filterClicked', { value: BuyBoxFilterOptions.MY_SERVICES });
	} else if (props.buyboxFilterType === BuyBoxFilterOptions.MY_SERVICES) {
		emit('filterClicked', { value: BuyBoxFilterOptions.ALL });
	}
}
// EXP_PROVIDER_BUYBOX_V2

onMounted(() => {
	if (activeRoute.value?.query.monetization_types === 'free') {
		emit('filterClicked', { value: BuyBoxFilterOptions.FREE, nonInteraction: true });
		return;
	}

	if (!filterOptions.value.find(filterOption => filterOption.value === props.buyboxFilterType)) {
		emit('filterClicked', { value: BuyBoxFilterOptions.ALL, nonInteraction: true });
		return;
	}
});

return { __sfc: true,HiddenHorizontalScrollbar, tc, activeRoute, props, emit, handleMyServicesChipClick, myServicesChip, providerBuyboxExpVariant, linearTVVariant, isLinearTVVariant1, isLinearTVVariant2, showLiveTvChip, filterOptions, handleFilterClick, handleMyServicesClick, BuyBoxFilterOptions, MyServicesChip }
}

})